// AddNarrationToolUI — branded tool-UI for the agent's `prepareNarration` tool
// call, rendered inline in the Aidea Assistant chat thread.
//
// The tool result is `{ feature:'sound', prompt, settings, openPopup:true }`.
// On the COMPLETE state we dispatch ONCE (useRef guard) via dispatchOpenAIPopup
// to open the AddSoundModal on the AI-voice tab pre-filled with the narration
// text and, when available, the course voice/service/language from settings.
// The popup owns generation + adding to scene — no extra path here.
//
// Clone of GenerateImageToolUI's OpenedState pattern (no picker, no image).
// Registration: makeAssistantToolUI({ toolName: "prepareNarration", render }).

import React, { useCallback, useEffect, useRef } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, SoundIcon, AlertIcon } from "../icons";
import { dispatchOpenAIPopup, shouldAutoOpenOnce } from "../aiPopupGateway";
import { OpenPopupButton } from "./OpenPopupButton";
import { mapAiProviderErrorMessage } from "../../../../common/ai_provider_errors.es6";

// Parse the tool result into { feature, prompt, settings, openPopup } | { error } | null.
// The backend sends a JSON string; be defensive on both string and object forms.
function parseNarrationResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  const openPopup = data.openPopup === true;
  const prompt = typeof data.prompt === "string" ? data.prompt.trim() : "";
  const settings = data.settings && typeof data.settings === "object" ? data.settings : {};
  if (!openPopup && !prompt) return null;
  return { prompt, settings, openPopup };
}

function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <SoundIcon size={12} />
      {label}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

function RunningState({ request }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          {request ? (
            <>
              Preparing narration for{" "}
              <span className="tw-font-medium tw-text-[var(--aidea-fg)]">"{request}"</span>
              {"…"}
            </>
          ) : (
            "Preparing narration…"
          )}
        </span>
      </div>
    </Card>
  );
}

function ErrorState({ message }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Narration" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">
          {mapAiProviderErrorMessage(message) ||
            message ||
            "Couldn't prepare the narration generator."}
        </span>
      </div>
    </Card>
  );
}

// Dispatches via the gateway exactly once (useRef guard), then shows a short
// branded confirmation. The popup owns generation + add-to-scene.
function OpenedState({ prompt, settings, toolCallId }) {
  const dispatchedRef = useRef(false);

  const doOpen = useCallback(
    () => dispatchOpenAIPopup({ feature: "sound", prompt, settings }),
    [prompt, settings]
  );

  // Auto-open ONCE per tool call (suppress re-open on chat close/reopen + restore).
  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    const key = toolCallId || `sound:${prompt || ""}`;
    if (!shouldAutoOpenOnce(key)) return undefined;
    const cancel = doOpen();
    return cancel;
  }, [doOpen, toolCallId, prompt]);

  const voiceLabel = settings && settings.voice ? settings.voice : "scene voice";

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Narration" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          <SoundIcon size={16} />
        </span>
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
            {"Opened the narration generator (voice: "}
            <span className="tw-font-medium">{voiceLabel}</span>
            {"). Review and Generate."}
          </p>
          <OpenPopupButton label="Open narration generator" onClick={doOpen} />
        </div>
      </div>
    </Card>
  );
}

export const AddNarrationToolUI = makeAssistantToolUI({
  toolName: "prepareNarration",
  render: ({ args, result, status, toolCallId }) => {
    const argRequest = args && typeof args.request === "string" ? args.request : "";

    if (status && status.type === "running") {
      return <RunningState request={argRequest} />;
    }

    const parsed = parseNarrationResult(result);
    if (!parsed) {
      return <ErrorState />;
    }
    if (parsed.error) {
      return <ErrorState message={parsed.error} />;
    }

    return (
      <OpenedState
        prompt={parsed.prompt || argRequest}
        settings={parsed.settings || {}}
        toolCallId={toolCallId}
      />
    );
  },
});

export default AddNarrationToolUI;
